import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Trash2, Copy, ArrowRight, Loader2
} from "lucide-react";

import {
  Card, CardContent, CardFooter, CardHeader, CardTitle
} from "../../../Components/StakingRisk/ui/card";
import {
  DeleteStakingRiskProductByIDThunk,
  DublicateStakingRiskThunk
} from "../../../Store/Thunks/StakingRiskThunk";
import { cn } from "../../../StakingRisk/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "../ui/alert-dialog";
import { getProductCompletionStats, getSafetyIndexInfo } from "../../../StakingRisk/utils/product-analytics.js";
import { DELETE_TYPES } from "../../../StakingRisk/constants/index.js";
import { Progress } from "../ui/progress.jsx";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const dublicatedStakingRiskId = useSelector(
    (state) => state.StakingRiskReducer.dublicatedStakingRiskId
  );

  useEffect(() => {
    if (dublicatedStakingRiskId)
      navigate(`/stakingrisk/product-detail/${dublicatedStakingRiskId}`);
  }, [dublicatedStakingRiskId]);

  const { completionPercentage } = getProductCompletionStats(product);
  const safety_index = product.safetyIndex;
  const safetyInfo = getSafetyIndexInfo(safety_index);

  const handleDelete = async () => {
    setIsDeleting(true);
   
    await dispatch(DeleteStakingRiskProductByIDThunk(product.id, DELETE_TYPES.SOFT_DELETE));
    setIsDeleting(false);
    setIsOpen(false);
  };

  return (
    <div className="relative group h-full">
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dispatch(DublicateStakingRiskThunk(product.id));
          }}
          className="h-7 w-7 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-gray-500 hover:text-blue-500 rounded-full"
          title="Copy Product"
        >
          <Copy className="h-4 w-4" />
        </button>

        <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
          <AlertDialogTrigger asChild>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(true);
              }}
              className="h-7 w-7 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/40 text-gray-500 hover:text-red-500 rounded-full"
              title="Delete product"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Move to Trash?</AlertDialogTitle>
              <AlertDialogDescription>
                This will move the staking product
                <span className="font-semibold"> "{product.name}"</span> to the trash box.
                You can restore it later or delete it permanently from the trash.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsOpen(false)} disabled={isDeleting}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Moving to Trash...
                  </>
                ) : (
                  "Move to Trash"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Link to={`/stakingrisk/product-detail/${product.id}`} className="block h-full">
        <Card className={`hover:shadow-xl transition-all h-full flex flex-col border-b border-l border-r dark:border-gray-800 border-t-4 ${safetyInfo.borderColor}`}>
          <CardHeader className="pb-3">
            <div className="flex justify-between items-start mb-1">
              <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                {product.name}
              </CardTitle>
               <div className={cn("px-2 py-0.5 text-xs font-semibold rounded-full inline-block", safetyInfo.bgColor, safetyInfo.color)}>
                 {safetyInfo.category}
            </div>
            </div>
          </CardHeader>

          <CardContent className="flex-grow flex flex-col justify-center items-center text-center p-4">
            <div className="text-4xl font-bold tracking-tight">
                {`${Math.round(safety_index)}%`}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Safety Index</p>
            <div className="w-full px-4">
              <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span>Assessment Progress</span>
                <span>{Math.round(completionPercentage)}%</span>
              </div>
              <Progress value={completionPercentage} className="h-1.5" />
            </div>
          </CardContent>

          <CardFooter className="flex justify-between items-center text-xs text-gray-400 dark:text-gray-500 p-4 pt-2">
            <span>Created: {format(new Date(product.createdAt), "dd-MMM-yyyy")}</span>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <span>View Details</span>
              <ArrowRight className="h-3 w-3" />
            </div>
          </CardFooter>
        </Card>
      </Link>
    </div>
  );
}